import { Plus, Trash } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Panel } from "@/components/admin/AdminUI";

export const OFFER_STORES = { app_store: "App Store", play_store: "Google Play" };
export const OFFER_PERIODS = ["week", "month", "3mo", "6mo", "year"];
const PRODUCT_RE = /^[A-Za-z0-9_.]{3,120}$/;
const OFFER_RE = /^[A-Za-z0-9_.-]{1,120}$/;
const FIELDS = ["store", "product_id", "offer_id", "display_price", "period", "label", "active"];

let seq = 0;
export const toOfferRow = (o) => ({ k: `o${++seq}`, ...Object.fromEntries(FIELDS.map((f) => [f, o[f] ?? (f === "active" ? true : "")])) });
export const newOfferRow = () => toOfferRow({ store: "app_store", period: "month", active: true });
export const offersPayload = (rows) => rows.map((r) => ({ ...Object.fromEntries(FIELDS.map((f) => [f, typeof r[f] === "string" ? r[f].trim() : r[f]])), label: r.label.trim() || null }));
const sig = (list) => JSON.stringify(list.map((o) => FIELDS.map((f) => (f === "label" ? o.label || null : o[f]))));
export const offersChanged = (orig, rows) => sig(orig) !== sig(offersPayload(rows));

export function validateOffers(rows) {
  const e = {};
  const seen = new Set();
  rows.forEach((r) => {
    const key = `${r.store}:${r.offer_id.trim()}`;
    const msg = !PRODUCT_RE.test(r.product_id.trim()) ? "Product ID: letters, numbers, _ and . (3+)." : !OFFER_RE.test(r.offer_id.trim()) ? "Offer ID: letters, numbers, _ . -" : !r.display_price.trim() ? "Enter the price exactly as the store shows it." : seen.has(key) ? "This offer ID is listed twice for that store." : null;
    seen.add(key);
    if (msg) e[r.k] = msg;
  });
  return e;
}

const cell = "h-9 bg-white text-sm";

function Row({ r, err, onChange, onRemove }) {
  const set = (f) => (e) => onChange(r.k, f, e?.target ? e.target.value : e);
  return (
    <>
      <tr className="border-b border-border last:border-0" data-testid={`offer-row-${r.k}`}>
        <td className="py-2 pl-4 pr-2"><select value={r.store} onChange={set("store")} className={`${cell} w-32 rounded-md border border-input px-2`} aria-label="Store" data-testid={`offer-store-${r.k}`}>{Object.entries(OFFER_STORES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></td>
        <td className="py-2 pr-2"><Input value={r.product_id} onChange={set("product_id")} list="offer-products" className={`${cell} font-mono`} aria-label="Product ID" data-testid={`offer-product-${r.k}`} /></td>
        <td className="py-2 pr-2"><Input value={r.offer_id} onChange={set("offer_id")} className={`${cell} font-mono`} aria-label="Offer ID" data-testid={`offer-id-${r.k}`} /></td>
        <td className="py-2 pr-2"><Input value={r.display_price} onChange={set("display_price")} placeholder="$4.99" className={`${cell} w-24`} aria-label="Display price" data-testid={`offer-price-${r.k}`} /></td>
        <td className="py-2 pr-2"><select value={r.period} onChange={set("period")} className={`${cell} w-24 rounded-md border border-input px-2`} aria-label="Period" data-testid={`offer-period-${r.k}`}>{OFFER_PERIODS.map((p) => <option key={p}>{p}</option>)}</select></td>
        <td className="py-2 pr-2"><Input value={r.label} onChange={set("label")} placeholder="Optional" className={cell} aria-label="Label" data-testid={`offer-label-${r.k}`} /></td>
        <td className="py-2 pr-2"><Switch checked={r.active} onCheckedChange={set("active")} aria-label="Active" data-testid={`offer-active-${r.k}`} /></td>
        <td className="py-2 pr-3"><Button size="icon" variant="ghost" className="cw-error" onClick={() => onRemove(r.k)} aria-label="Remove offer" data-testid={`offer-remove-${r.k}`}><Trash /></Button></td>
      </tr>
      {err && <tr><td colSpan={8} className="cw-error px-4 pb-2 text-xs" data-testid={`offer-error-${r.k}`}>{err}</td></tr>}
    </>
  );
}

export default function StoreOffersTable({ rows, errors, onChange, onAdd, onRemove, products }) {
  return (
    <section className="mt-10 space-y-3" data-testid="pricing-store-offers">
      <div className="flex items-end justify-between gap-3">
        <div><h2 className="text-lg font-semibold">Store offers</h2>
          <p className="cw-muted text-xs">Introductory or promotional offers you created in App Store Connect / Play Console. Promotions pick from these; the price is shown exactly as typed here.</p></div>
        <Button variant="outline" onClick={onAdd} data-testid="offer-add-button"><Plus />Add offer</Button>
      </div>
      <datalist id="offer-products">{products.map((p) => <option key={p} value={p} />)}</datalist>
      <Panel className="overflow-x-auto">
        {rows.length ? (
          <table className="w-full min-w-[960px] text-left" data-testid="store-offers-table">
            <thead><tr className="cw-muted border-b border-border text-xs"><th className="py-3 pl-4 font-medium">Store</th><th className="py-3 font-medium">Product ID</th><th className="py-3 font-medium">Offer ID</th><th className="py-3 font-medium">Display price</th><th className="py-3 font-medium">Period</th><th className="py-3 font-medium">Label</th><th className="py-3 font-medium">Active</th><th /></tr></thead>
            <tbody>{rows.map((r) => <Row key={r.k} r={r} err={errors[r.k]} onChange={onChange} onRemove={onRemove} />)}</tbody>
          </table>
        ) : <p className="cw-muted px-5 py-8 text-sm" data-testid="store-offers-empty">No store offers yet.</p>}
      </Panel>
    </section>
  );
}
